// Turns one report block (a pasted result) into HTML: figure, tiles, bars, pivot grid, line chart or table.
// Pure strings, so the app's editor and the exported file draw exactly the same thing. Styles: web/report.css (.rp).
import { parseTable, type Table } from "./sample";
import { compact, extent, formatNumber, humanize, isNull, niceTicks, plan, splitTotal, toNumber, toTime, type Plan, type Settings } from "./layout";

export const esc = (s: string) => s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);

export const MAX_TABLE_ROWS = 500;
// sequential blue, light -> dark (dataviz reference ramp); a pivot cell's shade is its share of the largest value
const RAMP = ["#e6f0fc", "#cde2fb", "#b7d3f6", "#9ec5f4", "#86b6ef", "#6da7ec", "#5598e7", "#3987e5", "#2a78d6", "#256abf", "#1c5cab", "#184f95"];
const DARK_FROM = 7; // from this step on, text in the cell is white

export type Rendered = { html: string; plan: Plan | null; table: Table | null; meta: boolean };

/** Text blocks: parse and lay out; text that isn't a table is shown as it is. */
export function renderText(text: string, name: string, settings: Settings = {}): Rendered {
  const asText = { html: `<pre class="rp-pre">${esc(text.replace(/\s+$/, ""))}</pre>`, plan: null, table: null, meta: false };
  try {
    const parsed = parseTable(text, name);
    if (!parsed || !parsed.rows.length) return asText;
    const { table, total } = splitTotal(parsed);
    const p = plan(table, settings, total);
    const meta = parsed.meta?.length && settings.details !== false ? metaHtml(parsed.meta) : "";
    return { html: meta + renderTable(table, p), plan: p, table, meta: !!parsed.meta?.length };
  } catch {
    return asText; // never let one odd paste blank the whole report
  }
}

export function renderTable(t: Table, p: Plan): string {
  switch (p.view) {
    case "figure": return figure(t, p);
    case "tiles": return tiles(t, p);
    case "bars": return bars(t, p);
    case "pivot": return pivot(t, p);
    case "line": return line(t, p);
    default: return table(t, p);
  }
}

/** The lines about the report that came above the table: a title, then "key: value" pairs. */
const metaHtml = (meta: string[][]) =>
  `<dl class="rp-meta">${meta.map((r) => (r.length === 1
    ? `<div class="rp-meta-t">${esc(r[0]!)}</div>`
    : `<div><dt>${esc(r[0]!)}</dt><dd>${esc(r.slice(1).join(" · "))}</dd></div>`)).join("")}</dl>`;

const show = (v: string | null, p: Plan, c: number) => {
  if (isNull(v)) return "";
  const col = p.columns[c]!;
  const n = col.kind === "number" ? toNumber(v) : null;
  return n === null ? v : formatNumber(n, col.decimals) + (v.trim().endsWith("%") ? "%" : "");
};
const head = (t: Table, c: number) => esc(humanize(t.columns[c] ?? ""));
const sum = (t: Table, c: number) => t.rows.reduce((a, r) => a + (toNumber(r[c] ?? null) ?? 0), 0);

function figure(t: Table, p: Plan) {
  const c = p.measure >= 0 ? p.measure : 0;
  const label = t.columns.length > 1 ? t.columns.map((_, i) => i).filter((i) => i !== c).map((i) => show(t.rows[0]![i]!, p, i)).filter(Boolean).join(" · ") : "";
  return `<div class="rp-figure"><div class="rp-figure-v">${esc(show(t.rows[0]![c] ?? null, p, c) || "–")}</div>
    <div class="rp-figure-l">${head(t, c)}${label ? ` <span>${esc(label)}</span>` : ""}</div></div>`;
}

function tiles(t: Table, p: Plan) {
  const r = t.rows[0]!;
  return `<div class="rp-tiles">${t.columns.map((_, c) => {
    const v = show(r[c] ?? null, p, c);
    const num = p.columns[c]!.kind === "number";
    return `<div class="rp-tile${num ? " num" : ""}"><div class="rp-tile-l">${head(t, c)}</div><div class="rp-tile-v">${esc(v || "–")}</div></div>`;
  }).join("")}</div>`;
}

/** Each row a bar for the chosen measure; other numbers sit as plain columns to the right. */
function bars(t: Table, p: Plan) {
  const m = p.measure, d = p.dims[0]!;
  const others = p.columns.flatMap((c, i) => (c.kind === "number" && i !== m ? [i] : []));
  const vals = t.rows.map((r) => toNumber(r[m] ?? null) ?? 0);
  const [min, max] = extent(vals);
  const lo = Math.min(0, min), hi = Math.max(0, max), span = hi - lo || 1;
  const zero = (-lo / span) * 100;
  const grid = `minmax(7em, 32%) minmax(160px, 1fr)${" minmax(5em, auto)".repeat(others.length)}`;
  const g = p.given;
  const tot = (c: number) => (g ? show(g[c] ?? null, p, c) : p.columns[c]!.additive ? formatNumber(sum(t, c), p.columns[c]!.decimals) : "");
  const totalRow = p.totals && (g || p.columns[m]!.additive)
    ? `<div class="rp-bar-row total"><span class="rp-bar-l">${esc((g && !isNull(g[d] ?? null) ? g[d] : "Total")!)}</span><span class="rp-bar-track"><span class="rp-bar-v plain">${esc(tot(m))}</span></span>${others.map((c) => `<span class="num">${esc(tot(c))}</span>`).join("")}</div>`
    : "";
  return `<div class="rp-bars" style="grid-template-columns:${grid}">
    <div class="rp-bars-h"><span>${head(t, d)}</span><span>${head(t, m)}</span>${others.map((c) => `<span class="num">${head(t, c)}</span>`).join("")}</div>
    ${t.rows.map((r, i) => {
      const v = vals[i]!;
      const left = v < 0 ? zero - (-v / span) * 100 : zero;
      const width = (Math.abs(v) / span) * 100;
      const label = show(r[d] ?? null, p, d) || "(empty)";
      const value = show(r[m] ?? null, p, m);
      return `<div class="rp-bar-row"><span class="rp-bar-l">${esc(label)}</span>
        <span class="rp-bar-track" title="${esc(`${label}: ${value}`)}"><span class="rp-bar-area">
          <span class="rp-bar${v < 0 ? " neg" : ""}" style="left:${left.toFixed(2)}%;width:${width.toFixed(2)}%"></span>
          <span class="rp-bar-v${v < 0 ? " neg" : ""}" style="left:${(v < 0 ? left : left + width).toFixed(2)}%">${esc(value)}</span></span></span>
        ${others.map((c) => `<span class="num">${esc(show(r[c] ?? null, p, c))}</span>`).join("")}</div>`;
    }).join("")}
    ${totalRow}
  </div>`;
}

function pivot(t: Table, p: Plan) {
  const [rd, cd] = p.dims as [number, number];
  const m = p.measure, dec = p.columns[m]!.decimals;
  const rowKeys = [...new Set(t.rows.map((r) => r[rd] ?? ""))];
  const colKeys = [...new Set(t.rows.map((r) => r[cd] ?? ""))];
  if (p.columns[cd]!.kind === "date" || p.columns[cd]!.kind === "label") colKeys.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  const cell = new Map<string, number>();
  for (const r of t.rows) { const v = toNumber(r[m] ?? null); if (v !== null) cell.set(`${r[rd]}\u0000${r[cd]}`, v); }
  const max = Math.max(0, extent([...cell.values()])[1]);
  const totals = p.totals && p.columns[m]!.additive;
  const fmt = (v: number) => formatNumber(v, dec);
  const shade = (v: number) => {
    if (v <= 0 || !max) return "";
    const i = Math.min(RAMP.length - 1, Math.round((v / max) * (RAMP.length - 1)));
    return ` style="background:${RAMP[i]}${i >= DARK_FROM ? ";color:#fff" : ""}"`;
  };
  const rowSum = (rk: string) => colKeys.reduce((a, ck) => a + (cell.get(`${rk}\u0000${ck}`) ?? 0), 0);
  const colSum = (ck: string) => rowKeys.reduce((a, rk) => a + (cell.get(`${rk}\u0000${ck}`) ?? 0), 0);
  const label = (v: string, c: number) => esc(show(v, p, c) || "(empty)");
  return `<div class="rp-scroll"><table class="rp-pivot">
    <caption>${head(t, m)} by ${head(t, rd)} and ${head(t, cd)}</caption>
    <thead><tr><th class="corner">${head(t, rd)} <span>/ ${head(t, cd)}</span></th>${colKeys.map((ck) => `<th>${label(ck, cd)}</th>`).join("")}${totals ? `<th class="tot">Total</th>` : ""}</tr></thead>
    <tbody>${rowKeys.map((rk) => `<tr><th>${label(rk, rd)}</th>${colKeys.map((ck) => {
      const v = cell.get(`${rk}\u0000${ck}`);
      return v === undefined ? `<td class="none"></td>` : `<td${shade(v)} title="${esc(`${show(rk, p, rd)} · ${show(ck, p, cd)}: ${fmt(v)}`)}">${fmt(v)}</td>`;
    }).join("")}${totals ? `<td class="tot">${fmt(rowSum(rk))}</td>` : ""}</tr>`).join("")}</tbody>
    ${totals ? `<tfoot><tr><th>Total</th>${colKeys.map((ck) => `<td>${fmt(colSum(ck))}</td>`).join("")}<td class="tot">${fmt(rowKeys.reduce((a, rk) => a + rowSum(rk), 0))}</td></tr></tfoot>` : ""}
  </table></div>`;
}

/** One measure over time: 2px line, a light wash under it, the last value labelled at its end. */
function line(t: Table, p: Plan) {
  const d = p.dims[0]!, m = p.measure, col = p.columns[d]!;
  const pts = t.rows
    .map((r) => ({ x: toTime(r[d] ?? null, col), y: toNumber(r[m] ?? null), label: r[d] ?? "" }))
    .filter((q): q is { x: number; y: number; label: string } => q.x !== null && q.y !== null)
    .sort((a, b) => a.x - b.x);
  if (pts.length < 2) return table(t, p);
  const W = 720, H = 280, L = 56, R = 72, T = 16, B = 34;
  const ys = pts.map((q) => q.y);
  const [ylo, yhi] = extent(ys);
  const ticks = niceTicks(Math.min(0, ylo), yhi);
  const y0 = ticks[0]!, y1 = ticks.at(-1)!;
  const x0 = pts[0]!.x, x1 = pts.at(-1)!.x;
  const X = (x: number) => L + ((x - x0) / (x1 - x0 || 1)) * (W - L - R);
  const Y = (y: number) => T + (1 - (y - y0) / (y1 - y0 || 1)) * (H - T - B);
  const path = pts.map((q, i) => `${i ? "L" : "M"}${X(q.x).toFixed(1)},${Y(q.y).toFixed(1)}`).join("");
  const area = `${path}L${X(x1).toFixed(1)},${Y(y0).toFixed(1)}L${X(x0).toFixed(1)},${Y(y0).toFixed(1)}Z`;
  const shortDate = (s: string) => (col.kind === "date" && /^\d{4}-\d{2}-01(?:[ T]00:00(?::00)?)?$/.test(s.trim()) ? s.trim().slice(0, 7) : s.trim().slice(0, 10));
  const every = Math.max(1, Math.ceil(pts.length / 6));
  const xLabels = pts.filter((_, i) => i % every === 0 || i === pts.length - 1)
    .filter((q, i, a) => i === a.length - 1 || X(a[a.length - 1]!.x) - X(q.x) > 60);
  const last = pts.at(-1)!;
  const dec = p.columns[m]!.decimals;
  return `<figure class="rp-line"><svg viewBox="0 0 ${W} ${H}" role="img" aria-label="${head(t, m)} by ${head(t, d)}">
    ${ticks.map((v) => `<line class="grid" x1="${L}" x2="${W - R}" y1="${Y(v).toFixed(1)}" y2="${Y(v).toFixed(1)}"/><text class="ytick" x="${L - 8}" y="${(Y(v) + 4).toFixed(1)}">${esc(compact(v))}</text>`).join("")}
    ${xLabels.map((q) => `<text class="xtick" x="${X(q.x).toFixed(1)}" y="${H - 10}">${esc(shortDate(q.label))}</text>`).join("")}
    <path class="area" d="${area}"/><path class="stroke" d="${path}"/>
    <circle class="end" cx="${X(last.x).toFixed(1)}" cy="${Y(last.y).toFixed(1)}" r="4.5"/>
    <text class="endv" x="${(X(last.x) + 10).toFixed(1)}" y="${(Y(last.y) + 4).toFixed(1)}">${esc(formatNumber(last.y, dec))}</text>
    ${pts.map((q) => `<circle class="hit" cx="${X(q.x).toFixed(1)}" cy="${Y(q.y).toFixed(1)}" r="9"><title>${esc(`${shortDate(q.label)}: ${formatNumber(q.y, dec)}`)}</title></circle>`).join("")}
  </svg><figcaption>${head(t, m)} by ${head(t, d)}</figcaption></figure>`;
}

/** Numbers right-aligned with separators, a quiet bar behind the chosen measure, a totals row. */
function table(t: Table, p: Plan) {
  const rows = t.rows.slice(0, MAX_TABLE_ROWS);
  const m = p.measure;
  const vals = m >= 0 ? t.rows.map((r) => toNumber(r[m] ?? null) ?? 0) : [];
  const [lo, hi] = vals.length ? extent(vals) : [0, 0];
  const max = Math.max(0, hi);
  const barOn = m >= 0 && t.rows.length >= 3 && lo >= 0 && max > 0;
  const summed = p.totals ? p.columns.map((c) => c.kind === "number" && c.additive) : [];
  const g = p.totals ? p.given : undefined;
  const hasTotals = !!g || summed.some(Boolean);
  const cell = (v: string | null, c: number) => {
    const col = p.columns[c]!;
    if (isNull(v)) return `<td class="null"></td>`;
    if (col.kind !== "number") return `<td>${esc(v)}</td>`;
    const bar = barOn && c === m ? `<span class="rp-db" style="width:${(((toNumber(v) ?? 0) / max) * 100).toFixed(1)}%"></span>` : "";
    return `<td class="num">${bar}<span>${esc(show(v, p, c))}</span></td>`;
  };
  return `<div class="rp-scroll"><table class="rp-table">
    <thead><tr>${t.columns.map((_, c) => `<th${p.columns[c]!.kind === "number" ? ' class="num"' : ""}>${head(t, c)}</th>`).join("")}</tr></thead>
    <tbody>${rows.map((r) => `<tr>${r.map(cell).join("")}</tr>`).join("")}</tbody>
    ${g ? `<tfoot><tr>${t.columns.map((_, c) => `<td${p.columns[c]!.kind === "number" ? ' class="num"' : ""}>${esc(show(g[c] ?? null, p, c))}</td>`).join("")}</tr></tfoot>`
    : hasTotals && t.rows.length >= 2 ? `<tfoot><tr>${t.columns.map((_, c) => summed[c]
      ? `<td class="num">${esc(formatNumber(sum(t, c), p.columns[c]!.decimals))}</td>`
      : `<td>${c === 0 ? "Total" : ""}</td>`).join("")}</tr></tfoot>` : ""}
  </table></div>${t.rows.length > MAX_TABLE_ROWS ? `<p class="rp-more">First ${MAX_TABLE_ROWS.toLocaleString("en-US")} of ${t.rows.length.toLocaleString("en-US")} rows.</p>` : ""}`;
}

/** Notes and the intro: plain text; a blank line starts a new paragraph. */
export const paragraphs = (s: string) =>
  s.trim() ? s.trim().split(/\n\s*\n/).map((para) => `<p>${esc(para).replace(/\n/g, "<br>")}</p>`).join("") : "";
